import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { $api, ApiError, unwrap } from '~/lib/api-client';
import { downloadBlob } from '~/lib/download-blob';
import { toIsoDateTime } from '~/lib/utils';
import { LiftStatusStorage } from 'Utils/Storage';

type ReportType = 'organisation' | 'lift';

const errorMessageForStatus = (status: number) => {
  switch (status) {
    case 400:
      return 'De gekozen periode is ongeldig of te lang';
    case 401:
    case 403:
      return 'Je hebt geen rechten om dit rapport te downloaden';
    case 404:
      return 'Geen gegevens gevonden voor dit rapport';
    case 413:
      return 'Het rapport is te groot, kies een kortere periode';
    default:
      return 'PDF genereren mislukt';
  }
};

const requestReport = (report: ReportType, subjectId: number) => {
  const from = toIsoDateTime(LiftStatusStorage.startDate, 'start');
  const to = toIsoDateTime(LiftStatusStorage.endDate, 'end');

  if (!from || !to) {
    throw new Error('Invalid report period');
  }

  switch (report) {
    case 'organisation':
      return unwrap(
        $api['/liftstatus/export/organisation-report/pdf'].POST({
          body: { filter: { organisationId: subjectId, from, to } },
          parseAs: 'blob',
        })
      );
    case 'lift':
      return unwrap(
        $api['/liftstatus/export/lift-details-report/pdf'].POST({
          body: { filter: { liftId: subjectId, from, to } },
          parseAs: 'blob',
        })
      );
  }
};

const useCallReportService = (options: {
  subjectId: number | null;
  report: ReportType;
}): [boolean, () => void] => {
  const { subjectId, report } = options;

  const mutation = useMutation({
    mutationFn: (id: number) => requestReport(report, id),
    onSuccess: (blob, id) => {
      downloadBlob(blob, `liftstatus-${report}-report-${id}.pdf`);
    },
    onError: (error: Error) => {
      console.error('[useCallReportService] error', error);

      const message = errorMessageForStatus(
        error instanceof ApiError ? error.status : 0
      );

      toast.error(message, { toastId: `report-error-${report}` });
    },
  });

  const fetchReport = () => {
    if (subjectId === null || subjectId === undefined) {
      return;
    }

    mutation.mutate(subjectId);
  };

  return [mutation.isPending, fetchReport];
};

export default useCallReportService;
